import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, Edit2, Trash2, X, Save, Receipt } from "lucide-react";
import CelebrationButton from "@/components/ui/CelebrationButton/CelebrationButton";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/table";
import Pagination from "./PortPlace/Pagination";

const getUnLocationColumns = (handlers) => {
  const { handleEdit, handleDelete } = handlers;

  return [
    {
      key: "code",
      label: "UN/LOCODE",
      render: (v) => (
        <span className="font-mono font-medium text-emerald-600 dark:text-emerald-400">
          {v?.code || "-"}
        </span>
      ),
    },
    {
      key: "name",
      label: "LOCATION NAME",
      render: (v) => (
        <span className="font-medium text-slate-800 dark:text-white">
          {v?.name || "-"}
        </span>
      ),
    },
    {
      key: "country",
      label: "COUNTRY",
      render: (v) => v?.country || "-",
    },
    {
      key: "type",
      label: "TYPE",
      render: (v) => v?.type || "-",
    },
    {
      key: "timezone",
      label: "TIMEZONE",
      render: (v) => <span className="font-mono">{v?.timezone || "-"}</span>,
    },
    {
      key: "status",
      label: "STATUS",
      render: (v) => (
        <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-semibold rounded-full">
          {v?.status || "-"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "ACTIONS",
      render: (v) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handleEdit(v)}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4 text-slate-500" />
          </button>

          <button
            onClick={() => handleDelete(v?.id)}
            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];
};

const ChargesCode = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordPerPage] = useState(10);
  const { addressList, pagination } = useSelector((state) => state.chargesCode);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    country: "",
    type: "Seaport",
    timezone: "",
    status: "Active",
  });
  const [errors, setErrors] = useState({});

  const { isAnimate } = useSelector((state) => state.toast);

  // Sample location data
  const [locations, setLocations] = useState([
    {
      id: 1,
      code: "USLAX",
      name: "Los Angeles",
      country: "United States",
      type: "Seaport",
      timezone: "PST",
      status: "Active",
    },
    {
      id: 2,
      code: "USNYC",
      name: "New York",
      country: "United States",
      type: "Seaport",
      timezone: "EST",
      status: "Active",
    },
    {
      id: 3,
      code: "CNSHA",
      name: "Shanghai",
      country: "China",
      type: "Seaport",
      timezone: "CST",
      status: "Active",
    },
    {
      id: 4,
      code: "NLRTM",
      name: "Rotterdam",
      country: "Netherlands",
      type: "Seaport",
      timezone: "CET",
      status: "Active",
    },
    {
      id: 5,
      code: "SGSIN",
      name: "Singapore",
      country: "Singapore",
      type: "Seaport",
      timezone: "SGT",
      status: "Active",
    },
    {
      id: 6,
      code: "AEDXB",
      name: "Dubai",
      country: "UAE",
      type: "Seaport",
      timezone: "GST",
      status: "Active",
    },
    {
      id: 7,
      code: "DEHAM",
      name: "Hamburg",
      country: "Germany",
      type: "Seaport",
      timezone: "CET",
      status: "Active",
    },
    {
      id: 8,
      code: "JPYOK",
      name: "Yokohama",
      country: "Japan",
      type: "Seaport",
      timezone: "JST",
      status: "Active",
    },
  ]);

  const locationTypes = [
    "Seaport",
    "Airport",
    "Inland Terminal",
    "Rail Terminal",
    "Warehouse",
  ];
  const timezones = [
    "PST",
    "EST",
    "CST",
    "CET",
    "SGT",
    "GST",
    "JST",
    "GMT",
    "IST",
    "AEST",
  ];

  const filteredLocations = locations.filter(
    (l) =>
      l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.country.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleAdd = () => {
    setEditingLocation(null);
    setFormData({
      code: "",
      name: "",
      country: "",
      type: "Seaport",
      timezone: "",
      status: "Active",
    });
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({ ...location });
    setErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.code.trim()) newErrors.code = "UN/LOCODE is required";
    if (!formData.name.trim()) newErrors.name = "Location name is required";
    if (!formData.country.trim()) newErrors.country = "Country is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    if (editingLocation) {
      setLocations(
        locations.map((l) =>
          l.id === editingLocation.id
            ? { ...formData, id: editingLocation.id }
            : l,
        ),
      );
    } else {
      setLocations([...locations, { ...formData, id: Date.now() }]);
    }
    setShowModal(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this location?")) {
      setLocations(locations.filter((l) => l.id !== id));
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) _getList(currentPage - 1);
  };

  const handleNext = () => {
    if (pagination?.nextPage > 0) _getList(pagination.nextPage);
  };
 const handleGoto = (page) => {
    _getList(page);
  };
  const authUser = JSON.parse(localStorage.getItem("authUser"))?.[0];
  const _getList = async (page = 1) => {
    setCurrentPage(page);
    dispatch(callLoader(1)); // optional loader

    try {
      const payload = {
        IPageNo: page,
        IPageRecords: recordsPerPage,
        SearchText: searchText,
        SalesPersonId: 0,
        PrefixLetter: selectedLetter,
        CompanyID: authUser?.companyId,
      };

      await dispatch(getAddressBookList(payload));
    } catch (error) {
      console.error("Error fetching address book list:", error);
      // optional: show toast / alert
    } finally {
      dispatch(callLoader(0));
    }
  };

  const handlers = {
    handleEdit,
    handleDelete,
  };

  const columns = getUnLocationColumns(handlers);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
            <Receipt className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
              Charges Code
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Product & Services{" "}
            </p>
          </div>
        </div>
        <button
          onClick={handleAdd}
          data-testid="add-location-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add New Item Services
        </button>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by Account Name,Charge Code, or Division..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
        </div>
      </div>

      {/* Table */}

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <DataTable
          data={filteredLocations || []}
          columns={columns}
          trClass="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30"
          thClass="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider"
          tdClass="px-4 py-4 text-sm text-slate-600 dark:text-slate-400"
          htrClass="bg-gradient-to-r from-slate-700 to-slate-800 text-white"
          loading={false}
          emptyText="No charges code found"
          isAplyingFilter={false}
          resetFilters={() => {}}
        />
      </div>

      {/* Pagination */}
      {filteredLocations.length > 0 && (
        <Pagination
          currentPage={currentPage}
          nextPage={pagination?.nextPage}
          recordsPerPage={recordsPerPage}
          setRecordsPerPage={setRecordPerPage}
          onPrevious={handlePrevious}
          onNext={handleNext}
            totalRecord={pagination?.totalRecord}
            handleGoto={handleGoto}
        />
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
              data-testid="location-modal"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">
                  {editingLocation
                    ? "Edit Product or Services"
                    : "Add Product or Services"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      UN/LOCODE <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          code: e.target.value.toUpperCase(),
                        });
                        setErrors({ ...errors, code: "" });
                      }}
                      className={`w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${errors.code ? "border-red-500" : "border-slate-200 dark:border-slate-600"}`}
                      placeholder="e.g., USLAX"
                    />
                    {errors.code && (
                      <p className="text-red-500 text-xs mt-1">{errors.code}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Location Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        setErrors({ ...errors, name: "" });
                      }}
                      className={`w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${errors.name ? "border-red-500" : "border-slate-200 dark:border-slate-600"}`}
                      placeholder="e.g., Los Angeles"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => {
                      setFormData({ ...formData, country: e.target.value });
                      setErrors({ ...errors, country: "" });
                    }}
                    className={`w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${errors.country ? "border-red-500" : "border-slate-200 dark:border-slate-600"}`}
                    placeholder="e.g., United States"
                  />
                  {errors.country && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.country}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                      className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    >
                      {locationTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Timezone
                    </label>
                    <select
                      value={formData.timezone}
                      onChange={(e) =>
                        setFormData({ ...formData, timezone: e.target.value })
                      }
                      className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    >
                      <option value="">Select timezone</option>
                      {timezones.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <CelebrationButton
                  onClick={() => handleSave()}
                  label={editingLocation ? "Update" : "Save"}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                  icon={<Save className="w-4 h-4" />}
                  isSuccess={isAnimate}
                />
                {/* <button
                  onClick={handleSave}
                  data-testid="save-location-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {editingLocation ? 'Update' : 'Save'}
                </button> */}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ChargesCode;

